import {AsyncPipe} from '@angular/common';
import {Component, DestroyRef, OnInit, inject} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Store} from '@ngrx/store';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {CardComponent} from '../../../../shared/components/card/card.component';
import {CisternActions} from '../../state/cistern.actions';
import {
  selectCisternFilter,
  selectCisternsTotalCount,
  selectFilteredCisternsWithCurrentPage,
  selectIsCisternsLoading
} from '../../state/cistern.selectors';
import {IconComponent} from '../../../../shared/icons/icon.component';
import {ViewPreferenceService} from '../../../../core/services/view-preference.service';
import {CisternItemRowComponent} from '../../components/cistern-item-row/cistern-item-row.component';
import { RouteConstants } from '../../../../shared/constants/route-constants';
import {CisternDialogResult, DialogUpdateCisternComponent} from '../../components/dialog-update-cistern/dialog-update-cistern.component';
import {catchError, EMPTY, filter, take} from 'rxjs';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { CisternItemCardComponent } from "../../components/cistern-item-card/cistern-item-card.component";
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {InputWrapperComponent} from '../../../../shared/components/input/input-wrapper/input-wrapper.component';
import {PaginationComponent} from '../../../../shared/components/pagination/pagination.component';
import {PageEvent} from '@angular/material/paginator';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {withResetPageOnOtherFilterChange} from '../../../../shared/utils/filter-utils';
import {CisternFilter} from '../../state/cistern.state';
import { TextWithIconComponent } from "../../../../shared/components/text-with-icon/text-with-icon.component";

@Component({
  selector: 'app-page-cistern-list',
  imports: [
    CardComponent,
    AsyncPipe,
    TranslatePipe,
    IconComponent,
    CisternItemRowComponent,
    CisternItemCardComponent,
    ReactiveFormsModule,
    InputWrapperComponent,
    PaginationComponent,
    TextWithIconComponent
],
  templateUrl: './page-cistern-list.component.html',
  styleUrl: './page-cistern-list.component.scss',
})
export class PageCisternListComponent implements OnInit {
  private static readonly viewPreferenceKey = 'cisterns.page-cistern-list';

  private _fb = inject(FormBuilder);
  private store = inject(Store);
  private dialog = inject(MatDialog);
  private translateService = inject(TranslateService);
  private readonly viewPreferenceService = inject(ViewPreferenceService);
  private destroyRef = inject(DestroyRef);

  loading$ = this.store.select(selectIsCisternsLoading);
  totalCount$ = this.store.select(selectCisternsTotalCount);
  cisternList$ = this.store.select(selectFilteredCisternsWithCurrentPage);
  isListView = this.viewPreferenceService.getIsListView(PageCisternListComponent.viewPreferenceKey, true);
  filterForm: FormGroup;

  constructor() {
    this.filterForm = this._fb.group({
      searchTerm: [undefined],
      page: [null],
      pageSize: [null],
    });
  }

  ngOnInit(): void {
    this.filterForm.valueChanges.pipe(
      withResetPageOnOtherFilterChange(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((newFilter) => {
      this.dispatchFilterUpdate(newFilter);
    });

    this.store.select(selectCisternFilter).pipe(
      takeUntilDestroyed(this.destroyRef),
      catchError(() => EMPTY),
    ).subscribe((filter) => {
      this.filterForm.patchValue(filter, {emitEvent: false});
    });

    this.dispatchFilterUpdate(this.filterForm.getRawValue());
  }

  toggleView(): void {
    this.isListView = !this.isListView;
    this.viewPreferenceService.setIsListView(PageCisternListComponent.viewPreferenceKey, this.isListView);
  }

  onDeleteCistern(id: string) {
    const dialogData: ConfirmDialogData = {
          title: this.translateService.instant('cisterns.delete.title'),
          body: this.translateService.instant('cisterns.delete.body'),
          action: 'danger',
          confirmLabel: this.translateService.instant('default.delete'),
        };

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
          data: dialogData,
        });

        dialogRef.afterClosed().pipe(
          take(1),
          filter((result): result is true => result === true),
        ).subscribe(() => {
          this.store.dispatch(CisternActions.public.deleteCisternById({id}));
        });
  }

  onChangePage(p: PageEvent) {
    this.filterForm.controls['page'].patchValue(p.pageIndex);
  }

  onCreateCistern() {
    const dialogRef = this.dialog.open(DialogUpdateCisternComponent, {
      width: '36rem',
      data: {mode: 'create'},
    });

    dialogRef.afterClosed().pipe(
      take(1),
      filter((result): result is CisternDialogResult => !!result),
    ).subscribe((result) => {
      if (result.id) {
        this.store.dispatch(CisternActions.public.updateCistern({
          id: result.id,
          requestDto: result.requestDto,
        }));
        return;
      }

      this.store.dispatch(CisternActions.public.createCistern({requestDto: result.requestDto}));
    });
  }

  onEditCistern(id: string) {
    this.cisternList$.pipe(take(1)).subscribe((cisterns) => {
      const cistern = cisterns.find((item) => item.id === id);
      if (!cistern) {
        return;
      }

      const dialogRef = this.dialog.open(DialogUpdateCisternComponent, {
        width: '36rem',
        data: {mode: 'update', cistern},
      });

      dialogRef.afterClosed().pipe(
        take(1),
        filter((result): result is CisternDialogResult => !!result),
      ).subscribe((result) => {
        if (result.id) {
          this.store.dispatch(CisternActions.public.updateCistern({
            id: result.id,
            requestDto: result.requestDto,
          }));
          return;
        }

        this.store.dispatch(CisternActions.public.createCistern({requestDto: result.requestDto}));
      });
    });
  }

  private dispatchFilterUpdate(rawFilter: {
    searchTerm?: string;
    page?: number | null;
    pageSize?: number | null;
  }) {
    const filter: CisternFilter = {
      searchTerm: rawFilter.searchTerm,
      page: rawFilter.page ?? undefined,
      pageSize: rawFilter.pageSize ?? undefined,
    };

    this.store.dispatch(CisternActions.public.updateCisternFilter({filter}));
  }

  protected readonly RouteConstants = RouteConstants;
}
